import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Banner, BlockStack, Divider, InlineStack, List, Modal, Text } from "@shopify/polaris";

function getAuthenticatedHeaders() {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");

  return new Headers({
    Authorization: "Bearer " + currentUser.token,
    "Content-Type": "application/json",
  });
}

async function sendcloudPickupRequest(url, { method = "GET", body } = {}) {
  const response = await fetch(url, {
    method,
    headers: getAuthenticatedHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const error = new Error(payload?.message || (typeof payload === "string" ? payload : "Sendcloud pickup request failed."));
    error.details = Array.isArray(payload?.details) ? payload.details : [];
    throw error;
  }

  return payload;
}

function formatPickupWindow(timeSlots) {
  if (!Array.isArray(timeSlots) || !timeSlots[0]?.start_at || !timeSlots[0]?.end_at) return null;

  const startDate = new Date(timeSlots[0].start_at);
  const endDate = new Date(timeSlots[0].end_at);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;

  const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
  const timeFormatter = new Intl.DateTimeFormat(undefined, { timeStyle: "short" });

  return `${dateFormatter.format(startDate)} · ${timeFormatter.format(startDate)} - ${timeFormatter.format(endDate)}`;
}

function buildOrderNumberPreview(orderNumbers = []) {
  if (!Array.isArray(orderNumbers) || orderNumbers.length === 0) return "No order numbers found";

  if (orderNumbers.length <= 3) return orderNumbers.join(", ");

  return `${orderNumbers.slice(0, 3).join(", ")} +${orderNumbers.length - 3} more`;
}

export default function ManualDhlPickupModal({ open, onClose, setToast }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [creatingPickup, setCreatingPickup] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setError(null);

    try {
      const response = await sendcloudPickupRequest("/api/sendcloud/pickups/manual-dhl-preview");
      setPreview(response);
    } catch (requestError) {
      setError(requestError);
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    setResult(null);
    loadPreview();
  }, [open, loadPreview]);

  const pickupWindow = useMemo(() => formatPickupWindow(preview?.logic?.time_slots), [preview]);
  const planCount = preview?.summary?.pickup_request_count || 0;
  const manualPickupDisabled = Boolean(preview?.test_mode) || planCount === 0 || loadingPreview;

  async function handleCreatePickup() {
    setCreatingPickup(true);
    setError(null);
    setResult(null);

    try {
      const response = await sendcloudPickupRequest("/api/sendcloud/pickups/manual-dhl", { method: "POST", body: {} });
      setResult(response);
      setPreview(response);
      setToast("ok");
    } catch (requestError) {
      setError(requestError);
      setToast("error");
    } finally {
      setCreatingPickup(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manual DHL pickup"
      size="large"
      primaryAction={{
        content: "Create manual DHL pickup",
        onAction: handleCreatePickup,
        loading: creatingPickup,
        disabled: manualPickupDisabled,
      }}
      secondaryActions={[{ content: "Close", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <BlockStack gap="100">
            <InlineStack gap="200" align="space-between">
              <Text variant="headingSm" as="h3">
                Scheduling logic
              </Text>
              {preview?.logic?.schedule_day_label && (
                <Badge tone={preview.logic.schedule_day === "same_day" ? "success" : "attention"}>
                  {preview.logic.schedule_day === "same_day" ? "Same-day pickup" : "Next-day pickup"}
                </Badge>
              )}
            </InlineStack>
            <List type="bullet">
              <List.Item>Automatic pickup creation on live DHL label creation stays active.</List.Item>
              <List.Item>This button creates additional manual pickup requests. It does not replace or disable the automatic flow.</List.Item>
              <List.Item>{preview?.logic?.explanation || "The pickup timing logic is loading."}</List.Item>
              <List.Item>{preview?.logic?.selection_rule || "We are loading the shipment selection rule."}</List.Item>
              {preview?.logic?.manual_note && <List.Item>{preview.logic.manual_note}</List.Item>}
            </List>
          </BlockStack>

          {pickupWindow && (
            <>
              <Divider />
              <BlockStack gap="100">
                <Text variant="headingSm" as="h3">
                  Current pickup window
                </Text>
                <Text>{pickupWindow}</Text>
                <Text tone="subdued">
                  {`Pickup timezone: ${preview?.logic?.time_zone || "Europe/Brussels"} · current local time: ${preview?.logic?.current_local_time || "--:--"} · cutoff: ${
                    preview?.logic?.same_day_cutoff || "15:00"
                  }`}
                </Text>
              </BlockStack>
            </>
          )}

          <Divider />

          <BlockStack gap="100">
            <Text variant="headingSm" as="h3">
              Current manual pickup preview
            </Text>
            {loadingPreview ? (
              <Text tone="subdued">Loading the current DHL pickup preview...</Text>
            ) : (
              <List type="bullet">
                <List.Item>{`Pickup requests to create: ${preview?.summary?.pickup_request_count || 0}`}</List.Item>
                <List.Item>{`Matched DHL shipments announced today: ${preview?.summary?.shipment_count || 0}`}</List.Item>
                <List.Item>{`Pickup parcels in request: ${preview?.summary?.parcel_count || 0}`}</List.Item>
                <List.Item>{`Total pickup weight: ${preview?.summary?.total_weight || "0.100"} kg`}</List.Item>
              </List>
            )}
          </BlockStack>

          {preview?.test_mode && (
            <Banner tone="warning" title="Sendcloud test mode is active">
              <p>Manual DHL pickup creation is disabled while Sendcloud test mode is active.</p>
            </Banner>
          )}

          {preview?.logic?.fallback_used && !loadingPreview && !preview?.test_mode && (
            <Banner tone="info" title="No live DHL shipments found, fallback pickup will be used">
              <p>No live DHL shipments announced today were found in Sendcloud. You can still request a manual pickup with the default Sendcloud sender address and configured DHL fallback values.</p>
            </Banner>
          )}

          {error && (
            <Banner tone="critical" title="Manual DHL pickup failed">
              <p>{error.message}</p>
              {(error.details || []).map((detail, index) => (
                <p key={`${detail.field || "manual-pickup"}_${index}`}>{`${detail.field ? `${detail.field}: ` : ""}${detail.message}`}</p>
              ))}
            </Banner>
          )}

          {result?.created_pickups?.length > 0 && (
            <Banner tone="success" title="Manual DHL pickup created">
              {result.created_pickups.map((pickup) => (
                <p key={`${pickup.pickup_id || pickup.reference || pickup.carrier_code}`}>
                  {`${pickup.carrier_code} pickup${pickup.pickup_id ? ` #${pickup.pickup_id}` : ""} created for ${pickup.parcel_count} parcel(s) / ${pickup.total_weight} kg.`}
                </p>
              ))}
            </Banner>
          )}

          {result?.errors?.length > 0 && (
            <Banner tone="critical" title="Some manual pickup requests could not be created">
              {result.errors.map((pickupError, index) => (
                <p key={`${pickupError.carrier_code || "manual-error"}_${index}`}>{`${pickupError.carrier_code || "dhl"}: ${pickupError.error?.message || "Unknown pickup error."}`}</p>
              ))}
            </Banner>
          )}

          {Array.isArray(preview?.plans) && preview.plans.length > 0 && (
            <>
              <Divider />
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">
                  Pickup requests that will be created
                </Text>
                {preview.plans.map((plan, index) => (
                  <BlockStack gap="50" key={`${plan.carrier_code}_${plan.sender_address?.postal_code || "sender"}_${index}`}>
                    <Text as="p" fontWeight="semibold">
                      {`${plan.carrier_code} · ${plan.parcel_count} parcel(s) · ${plan.total_weight} kg`}
                    </Text>
                    <Text tone="subdued">{plan.source === "fallback" ? "Source: fallback manual pickup defaults" : "Source: today's live DHL shipments"}</Text>
                    <Text tone="subdued">
                      {`${plan.sender_address?.company_name || plan.sender_address?.name || "Sender address"} · ${plan.sender_address?.postal_code || ""} ${
                        plan.sender_address?.city || ""
                      }`}
                    </Text>
                    <Text tone="subdued">{`Shipments: ${plan.shipment_count} · Orders: ${buildOrderNumberPreview(plan.order_numbers)}`}</Text>
                  </BlockStack>
                ))}
              </BlockStack>
            </>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
